import { useEffect } from "react";

// Injects a <script type="application/ld+json"> into <head> and cleans it up on unmount.
export const JsonLd = ({ id, data }) => {
  useEffect(() => {
    if (!data) return undefined;
    const scriptId = `jsonld-${id}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const e = document.getElementById(scriptId);
      if (e) e.remove();
    };
  }, [id, data]);
  return null;
};

// Builds FAQPage schema from an array of { q, a }.
export const buildFaqSchema = (faqArr = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqArr.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Builds Product/Offer schema for a property listing.
export const buildListingSchema = (property, url) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: property.title,
  description: property.description,
  image: property.images,
  category:
    property.listing_type === "mid_term" ? "Mid-term furnished rental (monthly)"
      : property.listing_type === "rental" ? "Short-term rental" : "Property for sale",
  brand: { "@type": "Brand", name: "Velmira Living" },
  offers: {
    "@type": "Offer",
    price: property.price,
    priceCurrency: property.price_unit,
    ...(property.listing_type === "mid_term" || property.listing_type === "rental"
      ? { priceSpecification: { "@type": "UnitPriceSpecification", price: property.price, priceCurrency: property.price_unit, unitCode: property.listing_type === "mid_term" ? "MON" : "DAY" } }
      : {}),
    availability: "https://schema.org/InStock",
    url,
  },
});

export default JsonLd;

// Breadcrumb schema from [{name, url}].
export const buildBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

// Service schema for a landing page.
export const buildServiceSchema = (name, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  url,
  provider: { "@type": "RealEstateAgent", name: "Velmira Living", url: "https://velmiraliving.com" },
  areaServed: ["Nice", "Cannes", "Antibes", "Villefranche-sur-Mer", "Beaulieu-sur-Mer", "Saint-Jean-Cap-Ferrat", "French Riviera", "Côte d'Azur"],
});

// Article/BlogPosting schema.
export const buildArticleSchema = (post, url) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.description,
  image: post.image,
  datePublished: post.date,
  dateModified: post.date,
  author: {
    "@type": "Person",
    name: "Görkem",
    url: "https://velmiraliving.com/en/author/gorkem",
    jobTitle: "Founder & Head of Operations",
    worksFor: { "@type": "Organization", name: "Velmira Living", url: "https://velmiraliving.com" },
  },
  publisher: {
    "@type": "Organization",
    name: "Velmira Living",
    logo: { "@type": "ImageObject", url: "https://velmiraliving.com/favicon.svg" },
  },
  mainEntityOfPage: url,
});
